import { useCallback, useState } from "react";

import {
    MeditationsAPI,
    uploadMeditationAudio,
} from "../infrastructure/api/Meditations.api";

export type AudioUploadState = {
    uploading: boolean;
    error: boolean;
    /** Push a recording or a chosen file to storage; resolves to its object name. */
    upload: (blob: Blob, meditationId?: string) => Promise<string>;
};

/**
 * Getting a recording into storage: ask for a slot, then PUT the bytes.
 *
 * Two steps rather than one because the bytes never pass through our API — a
 * twenty-minute take is tens of megabytes, and the only thing routing that
 * through the app server buys is a timeout. The API's part is authorising the
 * slot and naming the object; the browser's part is the transfer.
 */
export function useMeditationAudioUpload(): AudioUploadState {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(false);

    const upload = useCallback(async (blob: Blob, meditationId?: string) => {
        setUploading(true);
        setError(false);
        try {
            const { data } = await MeditationsAPI.audioUploadUrl({
                // `MediaRecorder` hands back "audio/webm;codecs=opus", and the
                // codec is not part of the type the API stores an extension
                // for. Sending it whole is what made an upload slot 400.
                content_type: blob.type.split(";")[0].trim(),
                meditation_id: meditationId,
            });
            await uploadMeditationAudio(data.upload_url, blob);
            return data.audio_path;
        } catch (cause) {
            setError(true);
            throw cause;
        } finally {
            setUploading(false);
        }
    }, []);

    return { uploading, error, upload };
}

/**
 * Fetch a recording as a same-origin blob URL.
 *
 * The signed URL from GCS would play fine in an `<audio>` element, but the
 * meeting needs to *publish* what is playing into the call, and a media
 * element fed a cross-origin source is tainted: `captureStream` on it yields
 * silence, with no error to explain why. Fetching the bytes ourselves and
 * handing the element a `blob:` URL makes the source same-origin, so the same
 * element can both play locally and feed the track the patient hears.
 */
export function useMeditationAudioSource() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const load = useCallback(async (meditationId: string) => {
        setLoading(true);
        setError(false);
        try {
            const { data } = await MeditationsAPI.audio(meditationId);
            return URL.createObjectURL(data);
        } catch (cause) {
            setError(true);
            throw cause;
        } finally {
            setLoading(false);
        }
    }, []);

    return { loading, error, load };
}

/**
 * The URL of a meditation's own bed, or null where it plays one of ours.
 *
 * A 404 is the ordinary answer — most meditations carry no bed of their own —
 * so it is not an error the therapist should hear about. The bytes come
 * through our API rather than from a signed URL, for the same reason the
 * recording does: the session captures what it plays.
 */
export async function meditationBackgroundUrl(
    meditationId: string
): Promise<string | null> {
    try {
        const { data } = await MeditationsAPI.backgroundAudio(meditationId);
        return URL.createObjectURL(data);
    } catch {
        return null;
    }
}
